import React, { createContext, useContext, useMemo, useState } from "react";

import { useQueries, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useParams } from "react-router-dom";

import { Agent } from "../models/agent";
import { Commodity } from "../models/commodity";
import { ContainerSize } from "../models/container.size";
import { Document } from "../models/document";
import { Milestone } from "../models/milestone";
import { Waybill } from "../models/waybill";
import AgentService from "../services/agent.service";
import DocumentService from "../services/document.service";
import WaybillsService from "../services/waybill.service";
import { useAppState } from "./StateContext";
import { Status } from "../util/enums";

type Request = Array<
  [setState: Function, execute: Function, ...executeArgs: any, enabled?: boolean]
>;

interface WaybillsContextProps {
  waybillId?: string;
  waybill?: Waybill;
  agents: Agent[];
  documents: Document[];
  commodities: Commodity[];
  containerSizes: ContainerSize[];
  milestones: Milestone[];
  isLoading?: boolean;
  hasUnsavedChanges?: boolean;
  setWaybill: (waybill: Waybill, noChanges?: boolean) => void;
  setHasUnsavedChanges: (value: boolean) => void;
}

const WaybillsContext = createContext<WaybillsContextProps>({} as WaybillsContextProps);

interface WaybillsProviderProps {
  children: React.ReactChild;
}

const WaybillsMaintenanceProvider = ({ children }: WaybillsProviderProps) => {
  const { waybillId } = useParams<{ waybillId?: string | undefined }>();
  const { currentUser } = useAppState();

  const [waybill, setWaybill] = useState<Waybill>({
    shipmentType: "Sea",
    containers: [
      {
        seal: "",
        container: "",
        grossWeight: "",
        size: "",
      },
    ],
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);

  const Resources: Request = useMemo(
    () => [
      [
        setCommodities,
        WaybillsService.getCommodities,
        { sortBy: JSON.stringify([["label", "ASC"]]) },
      ],
      [setContainerSizes, WaybillsService.getContainerSizes, { sortBy: JSON.stringify(["id"]) }],
      [setMilestones, WaybillsService.getMilestoneDates, { companyId: currentUser?.companyId }],
      [setAgents, AgentService.getAll, { createdCompanyId: currentUser?.companyId }],
      [setDocuments, DocumentService.getAll, { companyId: "", status: Status.ACTIVE }],
    ],
    []
  );

  const requestResults = useQueries({
    queries: Resources.map(([setState, execute, args, enabled = true], index) => ({
      queryKey: [index],
      queryFn: () =>
        execute({
          ...args,
        }),
      enabled,
      refetchOnWindowFocus: false,
      onSuccess: (response: AxiosResponse<(Commodity | ContainerSize | Milestone)[]>) => {
        if (response && response.data && response.data instanceof Array) setState(response.data);
      },
    })),
  });

  const { isFetching } = useQuery(
    ["waybills.list", waybillId],
    () =>
      WaybillsService.getById(Number(waybillId), {
        include: JSON.stringify([
          {
            association: "jobs",
            include: [
              { association: "itemsJob" },
              { association: "customer" },
              { association: "origins" },
              { association: "destinations" },
            ],
          },
          { association: "containers" },
          { association: "milestones" },
          { association: "shipmentTracking" },
          {
            association: "shipmentAgents",
            include: [{ association: "agent" }],
          },
          {
            association: "documents",
          },
        ]),
      }),
    {
      enabled: !!waybillId,
      onSuccess: (response) => {
        if (response && response?.data) setWaybill(response.data);
      },
    }
  );

  const isLoadingQueries = requestResults.some((result) => result.isFetching);

  return (
    <WaybillsContext.Provider
      value={{
        waybillId,
        waybill,
        agents,
        commodities,
        documents,
        containerSizes,
        milestones,
        isLoading: isFetching || isLoadingQueries,
        setWaybill: (waybill, noChanges) => {
          if (!noChanges) setHasUnsavedChanges(true);
          setWaybill(waybill);
        },
        hasUnsavedChanges,
        setHasUnsavedChanges,
      }}
    >
      {children}
    </WaybillsContext.Provider>
  );
};

export { WaybillsMaintenanceProvider };

export const useWaybillsMaintenanceContext = () => {
  const context = useContext(WaybillsContext);

  return context;
};
